import i18n from '@/user/plugins/i18n'
import { CertificationService } from '@/user/apis'
import { Model } from '@/models/model'
import { Matches, IsEmail, Length, IsNotEmpty } from 'class-validator-multi-lang'

/**
 * CertificationApplyForm 클래스는 인증 신청 양식을 나타냅니다.
 * Model 클래스를 상속받아 기본 모델 기능을 사용합니다.
 */
class CertificationApplyForm extends Model {
  // 신청자의 이름
  @Length(2, 100, {
    groups: ['submit'],
    message: i18n.global.t('common.certification.validation.error.name'),
  })
  name

  // 신청자의 영어 성
  @Matches(/^[a-zA-Z ]{1,50}$/, {
    message: i18n.global.t('common.certification.validation.error.engLastName'),
    groups: ['submit'],
  })
  nameEngFamily = ''

  // 신청자의 영어 이름
  @Matches(/^[a-zA-Z ]{1,100}$/, {
    message: i18n.global.t('common.certification.validation.error.engFirstName'),
    groups: ['submit'],
  })
  nameEngGiven = ''

  // 신청자의 전화번호
  @Matches(/^[0-9]{2,3}-[0-9]{3,4}-[0-9]{4}$/, {
    message: i18n.global.t('common.certification.validation.error.phoneNumber'),
    groups: ['submit'],
  })
  @IsNotEmpty({ groups: ['submit'] })
  phoneNumber = ''

  // 신청자의 이메일
  @IsEmail(undefined, {
    groups: ['submit'],
    message: i18n.global.t('common.certification.validation.error.email'),
  })
  email

  // 할인 코드
  discountCode = ''

  // 필수 약관 동의 여부
  terms = { privateInfoCollectionTerm: false }

  // 총 금액
  totalAmount

  // 시험 ID
  examId

  /**
   * CertificationApplyForm 생성자
   * @param {Object} memberInfo - 회원 정보 객체
   * @param {string} examId - 시험 ID
   * @param {number} totalAmount - 총 금액
   * @param {number} level - 시험 레벨
   */
  constructor(memberInfo, examId, totalAmount, level) {
    super()
    this.initialize(memberInfo, examId, totalAmount)

    this.name = memberInfo?.name || ''
    this.email = memberInfo?.email || ''
    this.totalAmount = totalAmount
    this.examId = examId
    this.level = level
  }

  clearForm() {
    this.nameEngFamily = ''
    this.nameEngGiven = ''
    this.discountCode = ''
    this.terms.privateInfoCollectionTerm = false
    this.$errors = {}
  }

  get validateTitles() {
    return {
      phoneNumber: i18n.global.t('certification.testApply.modal.phoneNumber'),
    }
  }

  async validateForSubmit() {
    const valid = await this.validate({
      groups: ['submit'],
    })

    if (this.terms.privateInfoCollectionTerm === false) {
      const errorMsg = i18n.global.t('common.certification.validation.error.terms')

      this.$errors['privateInfoCollectionTerm'] =
        this.terms.privateInfoCollectionTerm === false ? errorMsg : null

      return false
    }
    return valid
  }

  getErrorEnglishName() {
    return this.$errors['nameEngFamily'] || this.$errors['nameEngGiven']
  }

  async submit() {
    const body = {
      examId: this.examId,
      userName: this.name,
      email: this.email,
      englishFirstName: this.nameEngGiven,
      englishLastName: this.nameEngFamily,
      mobileNumber: this.phoneNumber,
      discountCode: this.discountCode,
      totalAmount: this.totalAmount,
      privateInfoCollectAgreed: this.terms.privateInfoCollectionTerm,
      level: this.level,
    }

    const res = await CertificationService.applyCertificationExam(this.examId, body)

    return new CertificationApplyResponse(res)
  }
}

class CertificationApplyResponse {
  /**
   * @param {object} data - 시험 접수 응답 데이터
   */
  constructor(data) {
    this.applicantUserData = new ApplicantUserData(data.applicantUserData)
    this.paymentData = new PaymentData(data.paymentData)
    this.paymentToken = data.paymentToken
  }
}

class ApplicantUserData {
  /**
   * @param {object} data - 자격증 시험 응시자 정보
   */
  constructor(data) {
    this.id = data.id // 응시자 아이디
    this.examId = data.examId // 자격증 시험 아이디
    this.userId = data.userId // 사용자 아이디
    this.userName = data.userName // 이름
    this.englishLastName = data.englishLastName // 영문 성
    this.englishFirstName = data.englishFirstName // 영문 이름
    this.mobileNumberLastDigits = data.mobileNumberLastDigits // 휴대폰 번호 뒷자리
    this.examNumber = data.examNumber // 수험번호
    this.appliedAt = data.appliedAt // 접수 일시
    this.canceledAt = data.canceledAt // 취소 일시
    this.applyState = data.applyState // 접수 상태
    this.progressState = data.progressState // 진행 상태
    this.score = data.score // 점수
    this.result = data.result // 결과
    this.privateInfoCollectAgreed = data.privateInfoCollectAgreed // 개인 정보 수집 동의 여부
  }
}
class PaymentData {
  /**
   * @param {object} data - 자격증 시험 결제 정보
   */
  constructor(data) {
    this.id = data.id // 결제 아이디
    this.applicantId = data.applicantId // 응시자 아이디
    this.pgTransactionId = data.pgTransactionId // PG 결제 번호
    this.transactionAt = data.transactionAt // 결제 일시
    this.discountCode = data.discountCode // 할인 코드
    this.totalAmount = data.totalAmount // 결제 금액
    this.state = data.state // 상태
    this.type = data.type // 결제 수단
  }
}

export default CertificationApplyForm
